
import axios from 'axios';
import localconfig from "@/config/local-config.json";
import { isInvalidRefreshTokenError, tokenTracker } from '@/services/token-refresh';
import { logErrorToDynatrace } from '@/utils/dynatraceLogger';

const httpClient = axios.create({
    // Only set baseURL in production or when explicitly configured
    baseURL: import.meta.env.DEV ? (import.meta.env.VITE_API_BASE_URL || '') : '',
    // timeout: 5000,
    maxRedirects: 3
});

// Track if we've already shown the unauthorized modal to prevent duplicates
let hasShownUnauthorizedModal = false;

httpClient.interceptors.request.use(
  async (config) => {
    const token = await getToken();
    const isAuthenticated = await checkAuthentication();
    
    if (token && config.headers ) {
      config.headers["X-Authorization"] = `Bearer ${token}`;
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    
    // Add x-auth header for unauthenticated requests
    if (!isAuthenticated && config.headers) {
      config.headers["x-auth"] = "false";
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);


// Response error handling with invalid refresh token detection
httpClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const errorMessage = err?.response?.data?.message || err?.message;

    console.error(`API Error: ${status ?? errorMessage}`);

    // Handle invalid/expired refresh token specifically - prevent retry loops
    if (isInvalidRefreshTokenError(err) || 
        (status === 401 && errorMessage?.toLowerCase().includes('invalid refresh'))) {
      
      if (!tokenTracker.isTokenInvalid()) {
        console.warn('Invalid refresh token detected - auto-logging out');
        tokenTracker.markTokenAsInvalid();

        // Use dynamic import to avoid circular dependency
        const { store } = await import('@/store/store');
        const { logout } = await import('@/store/slices/authSlice');
        const { clearUser } = await import('@/store/slices/userSlice');
        
        store.dispatch(logout());
        store.dispatch(clearUser());

        // Clear localStorage
        try {
          localStorage.removeItem('myassets_auth_state');
        } catch (e) {
          console.warn('Failed to clear auth state from localStorage', e);
        }
      }

      return Promise.reject(err);
    }

    // Handle 401 Unauthorized (general) - but only show modal once per session
    if (status === 401 && !hasShownUnauthorizedModal && !tokenTracker.isTokenInvalid()) {
      console.warn('Unauthorized access - showing modal');
      hasShownUnauthorizedModal = true;
      
      // Use dynamic import to avoid circular dependency
      const { store } = await import('@/store/store');
      const { setUnauthorizedModalOpen } = await import('@/store/slices/unauthorizedSlice');
      store.dispatch(setUnauthorizedModalOpen(true));
      logErrorToDynatrace({
              userID:localStorage.getItem('userID'),
              responseMessage: err?.response?.data?.message || err.message,
              responseStatus: err?.response?.status,
              responseData: err?.response?.data,
              requestUrl: `${err?.config?.baseURL || ''}${err?.config?.url || ''}`,
              requestMethod: err?.config?.method,
              requestPayload: err?.config?.data, // This is the request body
            });
    }

    return Promise.reject(err);
  }
);

const checkAuthentication = async () => {
    try {
      const { store } = await import('@/store/store');
      const state = store.getState();
      return state?.auth?.isLoggedIn ?? false;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
}

const getToken = async () => {
    // Placeholder for token retrieval logic
    try {
      const { store } = await import('@/store/store');
      const state = store.getState();
      let token: string | null = state?.auth?.token ?? null;

      token = import.meta.env.DEV ? localconfig.accessToken : token;

      // console.log('Retrieved token from store:', token ? 'Token found' : 'No token');
      return token;

    } catch (error) {
      console.error('Error retrieving token:', error);
    }
    return null;
}

// Reset the modal flag when user logs out
export const resetUnauthorizedModalFlag = () => {
  hasShownUnauthorizedModal = false;
};

export default httpClient;
